import {Injectable} from '@angular/core';
import {Actions, createEffect, ofType} from '@ngrx/effects';
import {Update} from '@ngrx/entity';
import {Action} from '@ngrx/store';
import {merge, Observable, of as observableOf} from 'rxjs';
import {catchError, map, switchMap, tap} from 'rxjs/operators';

import {Chacra, ChacraService} from '../../../../../chacra-core';
import * as chacrasActions from './chacras.actions';

import {NotificationService} from '../../../../../core/services';
import {LocalStorageService} from '../../../../../core/services';
import {AUTH_ROLE_ID} from '../../../../../core';

@Injectable()
export class ChacrasEffects {
  ChacrasLoadRequestEffect$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType<chacrasActions.ChacrasLoadRequestAction>(
      chacrasActions.ChacrasActionTypes.CHACRAS_LOAD_REQUEST
    ),
    switchMap((action) => {
      const rolId = this.localStorageService.getItem(AUTH_ROLE_ID);
      let params: any = {rolId};
      if(action.filter){
        params = {...params, filtros: {...action.filter}};
      }
      return this.chacrasService.getList(action.from, params).pipe(
        map(queryResults => {
          return new chacrasActions.ChacrasLoadSuccessAction({
            items: queryResults.items,
            meta: queryResults.meta,
          });
        }),
        catchError(error => {
          return observableOf(
            new chacrasActions.ChacrasLoadFailureAction({
              error
            })
          );
        })
      );
    })
  ));

  // get suelos
  ChacrasGetSuelosRequestEffect$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType<chacrasActions.ChacrasGetSuelosRequestAction>(
      chacrasActions.ChacrasActionTypes.CHACRAS_GETSUELOS_REQUEST
    ),
    map((action: chacrasActions.ChacrasGetSuelosRequestAction) => action.payload),

    switchMap(payload => this.chacrasService.getSuelos({
        ...payload.item,
        rendimientoId: payload.item.chacraNucleoId
      }).pipe(
      map(suelosResp => {
        // para suelo seleccionado
        let sueloSel = suelosResp.suelos.find(s => s.sueloId === suelosResp.sueloLimitanteId);
        let sueloChange = {};
        if (sueloSel) {
          sueloChange = {
            chacraSueloLimitanteFactorK: sueloSel.sueloFactorK,
            chacraSueloAsignadoFactorK: sueloSel.sueloFactorK,
            chacraSueloAsignadoId: sueloSel.sueloId,
            chacraSueloLimitanteId: sueloSel.sueloId,
            chacraSueloAsignadoDsc: sueloSel.sueloDesc,
            chacraSueloLimitanteDsc: sueloSel.sueloDesc,
            chacraSueloAsignadoTolerancia: sueloSel.sueloTolerancia,
            chacraSueloLimitanteTolerancia: sueloSel.sueloTolerancia,
            chacraSueloAsignadoSlopeMax: sueloSel.sueloSlopeMax,
            chacraSueloAsignadoSlopeMin: sueloSel.sueloSlopeMin,
            chacraSueloLimitanteSlopeMax: sueloSel.sueloSlopeMax,
            chacraSueloLimitanteSlopeMin: sueloSel.sueloSlopeMin,
            // chacraSueloAsignadoArea: sueloSel.sueloArea,
            // chacraSueloLimitanteArea: sueloSel.sueloArea,
          };
        }

        const uc: Update<Chacra> = {
          id: payload.item.rendimientoId,
          changes: {
            ...sueloChange,
            suelos: suelosResp.suelos,
            suelosLoading: false
          }
        };
        return new chacrasActions.ChacrasGetSuelosSuccessAction(
          {
            item: uc
          }
        );
      }),
      catchError(error => {
        const item: Update<Chacra> = {
          id: payload.item.rendimientoId,
          changes: {
            suelosLoading: false
          }
        };
        return observableOf(
          new chacrasActions.ChacrasGetSuelosFailureAction({
            error,
            item
          })
        );
      })
      )
    )
  ));

  ChacrasGetSuelosFailureEffect$: Observable<any> = createEffect(() => this.actions$.pipe(
    ofType(
      chacrasActions.ChacrasActionTypes.CHACRAS_GETSUELOS_FAILURE
    ),
    map((action: chacrasActions.ChacrasGetSuelosFailureAction) => action.payload.error),
    tap((error: string) => this.notificationService.info(`El servicio para la obtención de suelos falló. Vuelva a intentarlo con el botón "Obtener Suelos".`))
  ), {dispatch: false});

  // Get pendiente
  ChacrasGetLSRequestEffect$: Observable<Action> = createEffect(() => this.actions$.pipe(
    ofType<chacrasActions.ChacrasGetLSRequestAction>(
      chacrasActions.ChacrasActionTypes.CHACRAS_GETLS_REQUEST
    ),
    map(
      (action: chacrasActions.ChacrasGetLSRequestAction) =>
        action.payload
    ),
    switchMap(payload =>
      this.chacrasService
        .getLS({
          ...payload.item,
          rendimientoId: payload.item.chacraNucleoId
        })
        .pipe(
          map(getResults => {
            const uc: Update<Chacra> = {
              id: payload.item.rendimientoId,
              changes: {
                chacraFactorLSGeometriaLimitante: getResults.Geometry,
                chacraFactorLSGeometriaAsignado: getResults.Geometry,
                chacraFactorLSLimitante: Number(getResults.FactorLS),
                chacraFactorLSAsignado: Number(getResults.FactorLS),
                chacraFactorLLimitante: Number(getResults.FactorL),
                chacraFactorLAsignado: Number(getResults.FactorL),
                chacraFactorSLimitante: Number(getResults.FactorS),
                chacraFactorSAsignado: Number(getResults.FactorS),
                chacraPendienteLimitante: getResults.Slope,
                chacraPendienteAsignado: getResults.Slope,
                chacraLargoLimitante: getResults.SlopeLength,
                chacraLargoAsignado: getResults.SlopeLength,
                chacraFactorLSEsManual: false,
              }
            };
            return new chacrasActions.ChacrasGetLSSuccessAction({
              item: uc
            });

          }),
          catchError(error => {
            return observableOf(
              new chacrasActions.ChacrasGetLSFailureAction({
                error
              })
            );
          })
        )
    )
  ));

  // Copy Dibujo
  ChacrasFailureEffect$: Observable<any> = createEffect(() => this.actions$.pipe(
    ofType(
      chacrasActions.ChacrasActionTypes.CHACRAS_LOAD_FAILURE,
      chacrasActions.ChacrasActionTypes.CHACRAS_GETPADRONES_FAILURE
    ),
    map(
      (
        action:
          | chacrasActions.ChacrasLoadFailureAction
          | chacrasActions.ChacrasGetPadronesFailureAction
          | chacrasActions.ChacrasGetSuelosFailureAction
      ) => action.payload.error
    ),
    tap((error: string) => this.notificationService.error(error))
  ), {dispatch: false});

  // error

  constructor(
    private notificationService: NotificationService,
    private chacrasService: ChacraService,
    private actions$: Actions,
    private localStorageService: LocalStorageService
  ) {
  }
}
